import { api } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type {
  AnnualProcurementPlanCandidate,
  AnnualProcurementPlanDetail,
  AnnualProcurementPlanListItem,
  AnnualProcurementPlanRequest,
  AppDepartmentRow,
  PpmpDepartmentCandidate,
  PpmpDepartmentListItem,
  PpmpDepartmentRequest,
} from './procurementPlanTypes';

/**
 * The register files by FISCAL YEAR and by the row's own state.
 *
 * `status` is the shell's reserved axis — active or deleted, which Show deleted lifts — and
 * `fiscalYear` is read THROUGH the budget plan, because this record has no such column.
 */
export interface AnnualProcurementPlanListQuery extends ListQuery {
  fiscalYear?: string;
}

function toQueryString(query: AnnualProcurementPlanListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  /*
    Both facets are sent whenever the KEY is present, empty string included — an empty value is
    the reader saying "none of these", which matches no row. A truthiness check would drop it
    and the server would read the axis as unmentioned, which is the opposite filter.
  */
  if (query.status !== undefined) params.set('status', query.status);
  if (query.fiscalYear !== undefined) params.set('fiscalYear', query.fiscalYear);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

export const annualProcurementPlansApi = {
  clear: () => api<number>('/api/annual-procurement-plans/clear', { method: 'POST' }),

  list: (query: AnnualProcurementPlanListQuery, signal?: AbortSignal) =>
    api<PagedResult<AnnualProcurementPlanListItem>>(
      `/api/annual-procurement-plans${toQueryString(query)}`,
      { signal },
    ),

  get: (id: string, signal?: AbortSignal) =>
    api<AnnualProcurementPlanDetail>(`/api/annual-procurement-plans/${id}`, { signal }),

  /** Budget plans NOT already framed by a live APP — one per plan, so the rest would be refused. */
  candidates: (signal?: AbortSignal) =>
    api<AnnualProcurementPlanCandidate[]>('/api/annual-procurement-plans/candidates', { signal }),

  /**
   * The plan's department roster — every enrolled unit, left-joined to what it filed.
   *
   * Gated on `annual-procurement-plans.read` server-side, not the PPMP's key: the roster is
   * part of reading a plan.
   */
  roster: (id: string, signal?: AbortSignal) =>
    api<AppDepartmentRow[]>(`/api/annual-procurement-plans/${id}/roster`, { signal }),

  create: (request: AnnualProcurementPlanRequest) =>
    api<AnnualProcurementPlanDetail>('/api/annual-procurement-plans', { method: 'POST', body: request }),

  update: (id: string, request: AnnualProcurementPlanRequest) =>
    api<AnnualProcurementPlanDetail>(`/api/annual-procurement-plans/${id}`, {
      method: 'PUT',
      body: request,
    }),

  /** Soft delete. Its PPMPs are left exactly as they are. */
  deactivate: (id: string) => api<void>(`/api/annual-procurement-plans/${id}`, { method: 'DELETE' }),

  activate: (id: string) =>
    api<void>(`/api/annual-procurement-plans/${id}/restore`, { method: 'POST' }),
};

function departmentQueryString(query: ListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  if (query.status !== undefined) params.set('status', query.status);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

export const ppmpDepartmentsApi = {
  clear: () => api<number>('/api/ppmp-departments/clear', { method: 'POST' }),

  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<PpmpDepartmentListItem>>(
      `/api/ppmp-departments${departmentQueryString(query)}`,
      { signal },
    ),

  get: (id: string, signal?: AbortSignal) =>
    api<PpmpDepartmentListItem>(`/api/ppmp-departments/${id}`, { signal }),

  /** Active units NOT already enrolled — offering one the server would refuse is a rejection waiting. */
  candidates: (signal?: AbortSignal) =>
    api<PpmpDepartmentCandidate[]>('/api/ppmp-departments/candidates', { signal }),

  create: (request: PpmpDepartmentRequest) =>
    api<PpmpDepartmentListItem>('/api/ppmp-departments', { method: 'POST', body: request }),

  /** Withdraws it. Its already-filed PPMPs are left alone. */
  deactivate: (id: string) => api<void>(`/api/ppmp-departments/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/ppmp-departments/${id}/restore`, { method: 'POST' }),
};
